import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Typography, Divider } from "@mui/material";
import { getFileSize } from "../../../utils/file.js";
import { getScheduleHistoryApi, uploadNewScheduleApi, exportScheduleApi, exportScheduleTemplateApi } from "../../../api/schedule";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import Popup from "../../../component/popup/Popup.js";
import Table from "../../../component/table/MainTable.js";
import FileList from "../../../component/FileList.js";
import UploadNewFileButton from "../../../component/button/UploadNewFileButton.js";
import DownloadTemplateButton from "../../../component/button/DownloadTemplateButton.js";
import ExportDataButton from "../../../component/button/ExportDataButton.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";

const ManageSchedulePopup = ({ open, onClose, onUploadSuccess, user }) => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const [fileHistory, setFileHistory] = useState(null);
  const [latestFile, setLatestFile] = useState([]);
  const [templatePopup, setTemplatePopup] = useState(false);
  const [exportPopup, setExportPopup] = useState(false);
  const [isUploadButtonDisabled, setIsUploadButtonDisabled] = useState(false);
  const [isLoadingOperation, setIsLoadingOperation] = useState(false);
  const [showCloudDownloadButton, setShowCloudDownloadButton] = useState(false);
  const [modelHistoryHead, setModelHistoryHead] = useState([
    { id: "filename", label: "Archive File Name", align: "left" },
    { id: "fileSize", label: "Size", align: "left" },
    { id: "uploadDate", label: "Upload Date", align: "left", type: "dateWithTime" },
    { id: "uploadBy", label: "Upload By", align: "left" },
  ]);

  const templateHead = [
    { id: "format", label: "Format", align: "left" },
    { id: "filename", label: "File", align: "left" },
    { id: "icon", label: "", align: "center" },
  ];

  const [templateData, setTemplateData] = useState([
    {
      format: "MS Project",
      filename: "Schedule.mpp",
      fileURL: "",
    },
    {
      format: "P6",
      filename: "Schedule.xer",
      fileURL: "",
    },
  ]);

  const fetchTemplateUrls = useCallback(async () => {
    try {
      const mppRes = await exportScheduleTemplateApi(projectId, "mpp");
      const xerRes = await exportScheduleTemplateApi(projectId, "xer");
      setTemplateData([
        {
          format: "MS Project",
          filename: "Schedule.mpp",
          fileURL: mppRes?.data || "",
        },
        {
          format: "P6",
          filename: "Schedule.xer",
          fileURL: xerRes?.data || "",
        },
      ]);
    } catch {
      setTemplateData([
        {
          format: "MS Project",
          filename: "Schedule.mpp",
          fileURL: "",
        },
        {
          format: "P6",
          filename: "Schedule.xer",
          fileURL: "",
        },
      ]);
    }
  }, [projectId]);

  useEffect(() => {
    if (templatePopup) {
      fetchTemplateUrls();
    }
  }, [templatePopup, fetchTemplateUrls]);

  const readonly = !user?.editRight;

  const getTableData = useCallback(async () => {
    try {
      const result = await getScheduleHistoryApi(projectId);
      const data = result?.data;

      if (!data || !Array.isArray(data)) {
        showError("Invalid or empty table data received.");
        return;
      }

      const formattedData = data.map((item) => ({
        ...item,
        uploadDate: dayjs(item.uploadDate).format("YYYY-MM-DD HH:mm"),
        fileSize: item.fileSize ? getFileSize(item.fileSize) : 0,
      }));

      const activeItem = data.find((item) => item.status === "active");
      if (activeItem) {
        setLatestFile([activeItem]);
      }

      setFileHistory(formattedData);
    } catch {
      showError("Received table data occurred error.");
    }
  }, []);

  const uploadNewSchedule = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      setIsLoadingOperation(true);
      const formData = new FormData();
      formData.append("file", files[0]);
      try {
        const result = await uploadNewScheduleApi(projectId, formData);
        if (result) {
          showSuccess("The Schedule has been successfully uploaded to the project.");
          getTableData();
          onUploadSuccess();
        } else {
          showError("Upload failed.");
        }
      } catch (error) {
        if (error.status === 422 && error.response && error.response.data && error.response.data.detail) {
          showError(error.response.data.detail);
        } else if (error.status === 400 && error.response && error.response.data && error.response.data.detail) {
          let errorMessage = "";
          let hasError = false;

          const duplicates = (error.response.data.detail?.duplicateIds || []).filter((item) => {
            if (typeof item === "string") {
              return item.trim() !== "";
            }
            return item !== null && item !== undefined;
          });
          if (Array.isArray(duplicates) && duplicates.length > 0) {
            const duplicatesString = duplicates.join(",\n");
            errorMessage += `<strong>Following Activity ID are duplicated in the schedule:</strong><br />${duplicatesString}.<br /><strong>Please update the schedule to remove all duplicates before upload.</strong><br /><br />`;
            hasError = true;
          }

          const invalidContractors = (error.response.data.detail?.contractorsNotInProject || []).filter((item) => {
            if (typeof item === "string") {
              return item.trim() !== "";
            }
            return item !== null && item !== undefined;
          });
          if (Array.isArray(invalidContractors) && invalidContractors.length > 0) {
            const contractorsString = invalidContractors.join(",\n");
            errorMessage += `<strong>The following contractor(s) are not added to the project:</strong><br />${contractorsString}.<br /><strong>Please correct them before upload.</strong><br /><br />`;
            hasError = true;
          }

          const noActivityIdTasks = (error.response.data.detail?.tasksWithoutActivityId || []).filter((item) => {
            if (typeof item === "string") {
              return item.trim() !== "";
            }
            return item !== null && item !== undefined;
          });
          if (Array.isArray(noActivityIdTasks) && noActivityIdTasks.length > 0) {
            const tasksString = noActivityIdTasks.join(",\n");
            errorMessage += `<strong>Some Activity ID in the uploaded schedule are missing:</strong><br />${tasksString}.<br /><strong>Please update the schedule to include Activity ID in all rows before upload.</strong><br /><br />`;
            hasError = true;
          }

          const missingContractorInfo = (error.response.data.detail?.contractorMissing || []).filter((item) => {
            if (typeof item === "string") {
              return item.trim() !== "";
            }
            return item !== null && item !== undefined;
          });
          if (Array.isArray(missingContractorInfo) && missingContractorInfo.length > 0) {
            const missingContractorString = missingContractorInfo.join(",\n");
            errorMessage += `<strong>The following task(s) are missing contractors:</strong><br />${missingContractorString}.<br /><strong>Please ensure all tasks have contractors assigned before upload.</strong><br /><br />`;
            hasError = true;
          }

          if (hasError) {
            showError(errorMessage);
          } else {
            showError("Upload failed.");
          }
        } else {
          showError("Upload failed.");
        }
      } finally {
        setIsLoadingOperation(false);
      }
    }
  };

  const downloadFile = (item) => {
    setIsLoadingOperation(true);
    const link = document.createElement("a");
    link.href = item?.fileURL;
    link.download = item?.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      showSuccess("The file has been successfully downloaded.");
      setIsLoadingOperation(false);
    }, 3000);
  };

  const exportCurrentData = async (format) => {
    try {
      setIsLoadingOperation(true);
      const result = await exportScheduleApi(projectId, format);
      if (result) {
        const data = result?.data;
        const link = document.createElement("a");
        link.href = data;
        link.download = data?.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess("The file has been successfully downloaded.");
      } else {
        showError("Export failed.");
      }
    } catch {
      showError("Export failed.");
    } finally {
      setIsLoadingOperation(false);
    }
  };

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  useEffect(() => {
    if (fileHistory && fileHistory.length > 0) {
      setIsUploadButtonDisabled(true || readonly);
    } else {
      setIsUploadButtonDisabled(false || readonly);
    }
  }, [fileHistory, readonly]);

  useEffect(() => {
    if (!user) return;
    if (user?.company === "CLP" && user?.costAccess) {
      setShowCloudDownloadButton(true);
      setModelHistoryHead([
        { id: "filename", label: "Archive File Name", align: "left" },
        { id: "fileSize", label: "Size", align: "left" },
        { id: "uploadDate", label: "Upload Date", align: "left", type: "dateWithTime" },
        { id: "uploadBy", label: "Upload By", align: "left" },
        { id: "icon", label: "", align: "center" },
      ]);
    }
  }, [user]);

  return (
    <>
      <Popup
        open={open}
        onClose={onClose}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        popupStyle={{
          minWidth: 1000,
          maxWidth: 1000,
        }}
        title="Manage Schedule"
      >
        <Box maxHeight={800} overflow="auto">
          <Box
            sx={{
              mx: 4,
              my: 2,
              p: 2,
              background: "#F5F5F5",
              borderRadius: "10px",
            }}
          >
            <Typography variant="h6" component="span">
              Project Schedule
            </Typography>
            <Typography component="span">&nbsp;&nbsp;* Schedule files shall be in MS Project / P6 format</Typography>
            <Divider variant="fullWidth" />
            <Box sx={{ display: "flex", mb: 3, gap: 3 }}>
              <Typography component="span" sx={{ pt: 1.5 }}>
                current:
              </Typography>
              <FileList
                files={latestFile}
                onUpload={(event) => uploadNewSchedule(event)}
                showRemoveButton={false}
                showCloudDownloadButton={showCloudDownloadButton}
                onDownload={downloadFile}
                accept=".xer,.mpp"
                disabled={readonly}
              />
            </Box>
            <Table
              columns={modelHistoryHead}
              rows={fileHistory}
              iconColumnName="icon"
              headerStyles={{
                default: { backgroundColor: "#D0D5DE" },
              }}
              iconImage={"assets/icons/clonedownload.svg"}
              columnWidths={{
                icon: "100px",
                fileSize: "130px",
                uploadDate: "160px",
                uploadBy: "130px",
              }}
              sx={{ maxHeight: 200 }}
              onHistoryUploadSuccess={() => showSuccess("The file has been successfully downloaded.")}
            />
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={2}>
              <UploadNewFileButton
                accept=".xer,.mpp"
                disabled={isUploadButtonDisabled}
                onChange={(event) => {
                  uploadNewSchedule(event);
                }}
              ></UploadNewFileButton>
              <DownloadTemplateButton onClick={() => setTemplatePopup(true)} disabled={readonly}></DownloadTemplateButton>
              <ExportDataButton onClick={() => setExportPopup(true)}></ExportDataButton>
            </Box>
          </Box>
        </Box>
        <Box textAlign="center">
          <Button variant="outlined" color="inherit" onClick={onClose} sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2 }}>
            Back
          </Button>
        </Box>
      </Popup>
      <Popup
        open={templatePopup}
        onClose={() => setTemplatePopup(false)}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        title="Download Template"
      >
        <Box maxHeight={800} minHeight={200} overflow="auto" sx={{ m: 4 }}>
          <Table
            columns={templateHead}
            rows={templateData}
            iconColumnName="icon"
            headerStyles={{
              default: { backgroundColor: "#D0D5DE" },
            }}
            iconImage={"assets/icons/clonedownload.svg"}
            columnWidths={{
              icon: "100px",
            }}
            sx={{ maxHeight: 200 }}
            onHistoryUploadSuccess={() => showSuccess("The file has been successfully downloaded.")}
          />
        </Box>
      </Popup>
      <Popup
        open={exportPopup}
        onClose={() => setExportPopup(false)}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        title="Export Current Data"
      >
        <Box
          maxHeight={800}
          minHeight={100}
          overflow="auto"
          sx={{
            m: 4,
            display: "flex",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ExportDataButton onClick={() => exportCurrentData("mpp")} label={"Export MS Project Current Data"}></ExportDataButton>
          <ExportDataButton onClick={() => exportCurrentData("xer")} label={"Export P6 Current Data"}></ExportDataButton>
        </Box>
      </Popup>
      <FullScreenLoading isLoading={isLoadingOperation} />
    </>
  );
};

export default ManageSchedulePopup;
